import { useEffect, useRef, useState } from 'react';
import { userSettingsApiRef } from '@mercedes-benz/user-settings-react';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import {
  FEATURE_THEME,
  USER_PREFERENCES,
} from '@mercedes-benz/user-settings-common';
import { runSystemThemeChange } from '../systemThemeChange';

export interface ThemePreference {
  theme: string;
}

let initialSyncDone = false;

export function useSyncTheme() {
  const userSettingsApi = useApi(userSettingsApiRef);
  const appThemeApi = useApi(appThemeApiRef);
  const [currentTheme, setCurrentTheme] = useState<string | undefined>(
    undefined,
  );
  const syncingRef = useRef(false);

  // Initial sync: apply the backend-stored preference on first load.
  useEffect(() => {
    if (initialSyncDone) {
      return;
    }
    initialSyncDone = true;

    const syncInitial = async () => {
      syncingRef.current = true;
      try {
        const preference = await userSettingsApi.get({
          bucket: USER_PREFERENCES,
          key: FEATURE_THEME,
        });

        const value = preference?.value as unknown as ThemePreference;
        const activeTheme = appThemeApi.getActiveThemeId();

        if (value?.theme && value.theme !== activeTheme) {
          runSystemThemeChange(() => appThemeApi.setActiveThemeId(value.theme));
          setCurrentTheme(value.theme);
        } else if (!value?.theme && !activeTheme) {
          const installedThemes = appThemeApi.getInstalledThemes();
          runSystemThemeChange(() =>
            appThemeApi.setActiveThemeId(installedThemes[0]?.id),
          );
          setCurrentTheme(installedThemes[0]?.id);
        } else {
          setCurrentTheme(activeTheme);
        }
      } finally {
        syncingRef.current = false;
      }
    };

    syncInitial();
  }, [appThemeApi, userSettingsApi]);

  // Subscribe to theme changes and persist them to the backend.
  useEffect(() => {
    const subscription = appThemeApi.activeThemeId$().subscribe(themeId => {
      if (syncingRef.current) {
        return;
      }
      setCurrentTheme(themeId);
      userSettingsApi.set({
        bucket: USER_PREFERENCES,
        key: FEATURE_THEME,
        value: { theme: themeId },
      });
    });
    return () => subscription.unsubscribe();
  }, [appThemeApi, userSettingsApi]);

  return { currentTheme };
}
